// Sales Analytics: item sales, quantity and margin by period, from Business Central value entries and
// item ledger entries copied into courageMFG. Layout follows the BC-style design: filters, cues, charts,
// the margin bridge, what needs attention, the item table and a margin heatmap.
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError, type SalesAnalytics, type SalesItem } from "../api";
import { useLoad } from "../hooks";
import { bcUrl } from "../config";
import { ErrorBox } from "../ui";

const fmt0 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const fmt2 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n0 = (v: number | null | undefined) => (v === null || v === undefined ? "" : fmt0.format(v));
const n2 = (v: number | null | undefined) => (v === null || v === undefined ? "" : fmt2.format(v));
const p1 = (v: number | null | undefined) => (v === null || v === undefined ? "" : (v * 100).toFixed(1));
const k0 = (v: number) => fmt0.format(Math.round(v / 1000));
const usDate = (d?: string | null) => (d ? new Date(d.slice(0, 10) + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "");
const ago = (iso?: string) => { if (!iso) return ""; const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000); return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`; };
const itemUrl = (no: string) => `${bcUrl(30)}&filter=${encodeURIComponent(`'No.' IS '${no}'`)}`;
const monthName = (m: string) => new Date(m + "-01T00:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" });
const rel = (a: number, b: number) => (b ? (a - b) / Math.abs(b) : null);

function Delta({ v, pts, label }: { v: number | null; pts?: boolean; label: string }) {
  if (v === null || !isFinite(v)) return <span className="cue-n">No {label} figures</span>;
  const up = v >= 0;
  const text = pts ? `${Math.abs(v * 100).toFixed(1)} pts` : `${Math.abs(v * 100).toFixed(1)}%`;
  return <span className={`cue-n ${up ? "good" : "bad"}`}><span aria-hidden="true">{up ? "▲" : "▼"}</span> {up ? "Up" : "Down"} {text} vs {label}</span>;
}

/** Copies BC ledgers in steps until done, showing progress. */
function useSync(onDone: () => void) {
  const [state, setState] = useState<{ running: boolean; text: string; error: unknown }>({ running: false, text: "", error: null });
  const names: Record<string, string> = { items: "items", customers: "customers", value_entries: "value entries", item_ledger_entries: "item ledger entries" };
  const run = async (full = false) => {
    setState({ running: true, text: "Connecting to Business Central…", error: null });
    let stage: string | null = null, first = true;
    const totals: Record<string, number> = {};
    try {
      for (let i = 0; i < 200; i++) {
        const r = await api.analyticsSync({ full: full && first, stage });
        first = false;
        for (const x of r.results) totals[x.entity] = (totals[x.entity] ?? 0) + x.added;
        const last = r.results[r.results.length - 1];
        setState({ running: !r.done, text: `Copying ${names[last?.entity ?? ""] ?? "entries"}: ${fmt0.format(totals[last?.entity ?? ""] ?? 0)} so far`, error: null });
        if (r.done) break;
        stage = r.next;
      }
      setState({ running: false, text: "", error: null });
      onDone();
    } catch (e) { setState({ running: false, text: "", error: e }); }
  };
  return { ...state, run };
}

function exportCsv(items: SalesItem[], name: string) {
  const head = ["Item No.", "Description", "Item Category", "Quantity", "Net Sales", "Gross Margin", "Margin %", "Change vs Comparison (pts)", "Avg. Unit Price", "Avg. Unit Cost", "ABC Class"];
  const esc = (v: unknown) => { const s = v === null || v === undefined ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const rows = items.map((x) => [x.itemNo, x.description, x.category ?? "", x.qty, x.sales.toFixed(2), x.gm.toFixed(2), x.gmPct === null ? "" : (x.gmPct * 100).toFixed(1), x.gmPctChange === null ? "" : (x.gmPctChange * 100).toFixed(1), x.avgUnitPrice?.toFixed(2) ?? "", x.avgUnitCost?.toFixed(2) ?? "", x.abc]);
  const blob = new Blob([[head, ...rows].map((r) => r.map(esc).join(",")).join("\r\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function SalesChart({ d }: { d: SalesAnalytics }) {
  const s = d.series!;
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...s.flatMap((p) => [p.sales, p.cmpSales]));
  const nice = (() => { const e = Math.pow(10, Math.floor(Math.log10(max))); const m = max / e; return (m <= 2 ? 2 : m <= 5 ? 5 : 10) * e; })();
  const cmpLabel = d.query?.compare === "pp" ? "Prior period" : "Prior year";
  return (
    <div className="chart">
      <div className="chart-h">
        <h2>Net sales by {d.query?.groupBy === "quarter" ? "quarter" : "month"} (USD thousands)</h2>
        <span className="legend"><span><i style={{ background: "var(--viz-1)" }} />Selected period</span><span><i style={{ background: "var(--viz-cmp)" }} />{cmpLabel}</span></span>
      </div>
      <div className="table-wrap">
        <div className="bars" style={{ gridTemplateColumns: `44px repeat(${s.length}, minmax(28px, 1fr))` }} onMouseLeave={() => setHover(null)}>
          <div className="axis"><span>{k0(nice)}</span><span>{k0(nice / 2)}</span><span>0</span></div>
          {s.map((p, i) => (
            <div key={p.period} className={`bar-col ${hover === i ? "hover" : ""}`} onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} tabIndex={0}
              aria-label={`${p.label}: ${n0(p.sales)} USD, ${cmpLabel.toLowerCase()} ${n0(p.cmpSales)} USD`}>
              <span className="bar cur" style={{ height: `${(p.sales / nice) * 100}%` }} />
              <span className="bar cmp" style={{ height: `${(p.cmpSales / nice) * 100}%` }} />
              {hover === i && (
                <span className="tip" role="presentation">
                  <b>{p.label}</b>
                  <span>Net sales {n0(p.sales)}</span>
                  <span>{cmpLabel} {n0(p.cmpSales)}</span>
                  <span>Margin {p1(p.gmPct)}%</span>
                </span>
              )}
            </div>
          ))}
          <span />
          {s.map((p) => <span key={p.period} className="xlab">{p.label}</span>)}
        </div>
      </div>
    </div>
  );
}

function MarginChart({ d }: { d: SalesAnalytics }) {
  const s = d.series!.filter((p) => p.gmPct !== null);
  const target = d.marginTarget ?? 0.3;
  if (s.length < 2) return null;
  const vals = [...s.map((p) => p.gmPct as number), target];
  const lo = Math.floor(Math.min(...vals) * 100 - 1), hi = Math.ceil(Math.max(...vals) * 100 + 1);
  const W = 860, H = 120, x0 = 34, x1 = W - 12;
  const x = (i: number) => x0 + (i * (x1 - x0)) / Math.max(1, s.length - 1);
  const y = (v: number) => 12 + ((hi - v * 100) / (hi - lo)) * H;
  const minI = s.reduce((m, p, i) => ((p.gmPct as number) < (s[m].gmPct as number) ? i : m), 0);
  const first = s[0].gmPct as number, last = s[s.length - 1].gmPct as number;
  const below = s.filter((p) => (p.gmPct as number) < target).length;
  const moved = Math.abs((last - first) * 100);
  const summary = `${moved < 0.05 ? "Level" : `${last >= first ? "Up" : "Down"} ${moved.toFixed(1)} points`} from ${s[0].label} to ${s[s.length - 1].label}. ${below ? `Below the ${(target * 100).toFixed(0)}% target in ${below} of ${s.length} periods.` : `At or above the ${(target * 100).toFixed(0)}% target throughout.`}`;
  return (
    <div className="chart">
      <div className="chart-h"><h2>Gross margin % by {d.query?.groupBy === "quarter" ? "quarter" : "month"}</h2></div>
      <p className="chart-sub">{summary}</p>
      <div className="table-wrap">
        <svg viewBox={`0 0 ${W} ${H + 30}`} role="img" aria-label={`Gross margin percent by period. ${summary}`} style={{ width: "100%", minWidth: 560, height: H + 30, display: "block" }}>
          <line x1={x0} x2={x1} y1={y(target)} y2={y(target)} stroke="var(--warn-line)" strokeDasharray="4 4" />
          <text x={x1} y={y(target) - 6} textAnchor="end" fontSize="12" fill="var(--fg-2)">Target {(target * 100).toFixed(1)}%</text>
          <text x={0} y={y(hi / 100) + 4} fontSize="12" fill="var(--fg-3)">{hi}</text>
          <text x={0} y={y(lo / 100) + 4} fontSize="12" fill="var(--fg-3)">{lo}</text>
          <line x1={x0} x2={x1} y1={y(lo / 100)} y2={y(lo / 100)} stroke="var(--stroke)" />
          <polyline points={s.map((p, i) => `${x(i)},${y(p.gmPct as number)}`).join(" ")} fill="none" stroke="var(--viz-1)" strokeWidth="2" strokeLinejoin="round" />
          {s.map((p, i) => <circle key={p.period} cx={x(i)} cy={y(p.gmPct as number)} r={i === 0 || i === minI || i === s.length - 1 ? 4 : 2.5} fill="var(--viz-1)" stroke="#fff" strokeWidth="2"><title>{`${p.label}: ${p1(p.gmPct)}%`}</title></circle>)}
          {[0, minI, s.length - 1].filter((v, i, a) => a.indexOf(v) === i).map((i) => (
            <text key={i} x={x(i)} y={y(s[i].gmPct as number) + 18} textAnchor={i === 0 ? "start" : i === s.length - 1 ? "end" : "middle"} fontSize="12" fill="var(--fg)">{p1(s[i].gmPct)}{i === minI && i !== 0 && i !== s.length - 1 ? " low" : ""}</text>
          ))}
        </svg>
      </div>
    </div>
  );
}

function Bridge({ d }: { d: SalesAnalytics }) {
  const b = d.bridge!;
  const rows = [
    { name: "Volume", v: b.volume, note: "more or fewer units at last period's margin" },
    { name: "Price", v: b.price, note: "change in selling price per unit" },
    { name: "Product mix", v: b.mix, note: "shift between higher and lower margin items" },
    { name: "Cost", v: b.cost, note: "change in posted cost per unit" },
    { name: "New items", v: b.newItems, note: "sold only in the selected period" },
    { name: "Items no longer sold", v: b.lostItems, note: "sold only in the comparison period" },
  ].filter((r) => Math.abs(r.v) >= 0.5);
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.v)));
  const net = b.gm1 - b.gm0;
  const biggest = [...rows].sort((a, c) => Math.abs(c.v) - Math.abs(a.v))[0];
  return (
    <section className="panel pad">
      <h2 className="ph">Gross margin bridge</h2>
      <p className="chart-sub">{d.query?.compare === "pp" ? "Prior period" : "Prior year"} {n0(b.gm0)} to selected period {n0(b.gm1)} (USD)</p>
      <div className="bridge">
        {rows.map((r) => (
          <div key={r.name} className="bridge-row" title={r.note}>
            <span>{r.name}</span>
            <span className="neg">{r.v < 0 && <i style={{ width: `${(Math.abs(r.v) / max) * 100}%` }} />}</span>
            <span className="pos">{r.v >= 0 && <i style={{ width: `${(r.v / max) * 100}%` }} />}</span>
            <span className="r num">{r.v >= 0 ? "+" : "−"}{n0(Math.abs(r.v))}</span>
          </div>
        ))}
        <div className="bridge-row total"><span>Net change</span><span /><span /><span className="r num">{net >= 0 ? "+" : "−"}{n0(Math.abs(net))}</span></div>
      </div>
      {biggest && <p className="chart-sub" style={{ marginTop: 10 }}>The largest driver is {biggest.name.toLowerCase()}, {biggest.v >= 0 ? "adding" : "taking"} {n0(Math.abs(biggest.v))} USD.</p>}
    </section>
  );
}

function Attention({ d, setParam }: { d: SalesAnalytics; setParam: (k: string, v: string) => void }) {
  const a = d.attention!;
  const target = ((d.marginTarget ?? 0.3) * 100).toFixed(0);
  return (
    <section className="panel pad">
      <h2 className="ph">Needs attention</h2>
      <div className="att">
        <span className="att-h">Cost up, price unchanged: {a.costUp.length} {a.costUp.length === 1 ? "item" : "items"}</span>
        {a.costUp.length ? (
          <>
            <ul>{a.costUp.map((x) => <li key={x.itemNo}><a href={itemUrl(x.itemNo)} target="_blank" rel="noopener noreferrer">{x.itemNo}</a> {x.description}: unit cost {x.costChange >= 0 ? "+" : ""}{(x.costChange * 100).toFixed(1)}%, price {x.priceChange === null ? "unchanged" : `${x.priceChange >= 0 ? "+" : ""}${(x.priceChange * 100).toFixed(1)}%`}</li>)}</ul>
            <span className="att-n">Last three months against the three before.</span>
            <a href={bcUrl(31)} target="_blank" rel="noopener noreferrer">Review these items in Business Central</a>
          </>
        ) : <span className="att-n">No item's unit cost rose 5% or more without a price change in the last three months.</span>}
      </div>
      <div className="att">
        <span className="att-h">Customers who stopped buying: {a.lapsed.length}</span>
        {a.lapsed.length ? (
          <>
            <ul>{a.lapsed.map((x) => <li key={x.customerNo + x.itemNo}><button className="linkish" onClick={() => setParam("customer", x.customerNo)}>{x.customerName}</button> bought {x.itemNo} {x.description} in {x.monthsBought} months, last on {usDate(x.lastOrder)}.</li>)}</ul>
            <Link to="/opportunities/new">Create a follow-up opportunity</Link>
          </>
        ) : <span className="att-n">No customer has stopped buying an item they ordered regularly.</span>}
      </div>
      <div className="att last">
        <span className="att-h">Invoice lines below {target}% margin: {a.belowFloor.lines}</span>
        {a.belowFloor.lines ? (
          <span className="att-n">{n0(a.belowFloor.sales)} USD of sales since {usDate(a.belowFloor.from)}{a.belowFloor.customers.length ? `, mostly ${a.belowFloor.customers.map((c) => c.name).join(", ")}` : ""}.</span>
        ) : <span className="att-n">Every invoice line in the last 90 days met the target.</span>}
      </div>
    </section>
  );
}

type SortKey = "itemNo" | "qty" | "sales" | "gm" | "gmPct" | "gmPctChange" | "avgUnitPrice" | "abc";
function ItemTable({ d }: { d: SalesAnalytics }) {
  const [sort, setSort] = useState<{ k: SortKey; desc: boolean }>({ k: "sales", desc: true });
  const [all, setAll] = useState(false);
  const rows = useMemo(() => {
    const r = [...(d.items ?? [])];
    r.sort((a, b) => { const x = a[sort.k] ?? -Infinity, y = b[sort.k] ?? -Infinity; const c = x < y ? -1 : x > y ? 1 : 0; return sort.desc ? -c : c; });
    return r;
  }, [d.items, sort]);
  const shown = all ? rows : rows.slice(0, 25);
  const th = (k: SortKey, label: string, right = true) => (
    <th className={right ? "r" : ""} aria-sort={sort.k === k ? (sort.desc ? "descending" : "ascending") : "none"}>
      <button className="th-sort" onClick={() => setSort((s) => ({ k, desc: s.k === k ? !s.desc : k !== "itemNo" && k !== "abc" }))}>{label}{sort.k === k ? (sort.desc ? " ↓" : " ↑") : ""}</button>
    </th>
  );
  const cmp = d.query?.compare === "pp" ? "PP" : "PY";
  return (
    <section className="panel">
      <div className="panel-h">
        <h2 className="ph">Sales by item</h2>
        <span className="muted">{shown.length === d.itemCount ? `${d.itemCount} ${d.itemCount === 1 ? "item" : "items"}` : `Top ${shown.length} of ${d.itemCount} items by net sales`} {rows.length > 25 && <button className="linkish" onClick={() => setAll(!all)}>{all ? "Show top 25" : "Show all"}</button>}</span>
      </div>
      <div className="table-wrap pad-x">
        <table className="dgrid num-grid">
          <thead><tr>
            {th("itemNo", "Item No.", false)}<th>Description</th><th>Item Category</th>
            {th("qty", "Quantity")}{th("sales", "Net Sales")}{th("gm", "Gross Margin")}{th("gmPct", "Margin %")}{th("gmPctChange", `Change vs ${cmp}`)}{th("avgUnitPrice", "Avg. Unit Price")}<th className="r">Avg. Unit Cost</th>{th("abc", "ABC Class", false)}
          </tr></thead>
          <tbody>{shown.map((x) => (
            <tr key={x.itemNo}>
              <td><a href={itemUrl(x.itemNo)} target="_blank" rel="noopener noreferrer" title="Item card in Business Central">{x.itemNo}</a></td>
              <td>{x.description}</td><td>{x.category ?? ""}</td>
              <td className="r">{n0(x.qty)}</td><td className="r">{n0(x.sales)}</td><td className="r">{n0(x.gm)}</td>
              <td className={`r ${x.gmPct !== null && x.gmPct < (d.marginTarget ?? 0.3) ? "below" : ""}`}>{p1(x.gmPct)}</td>
              <td className={`r ${x.gmPctChange === null ? "" : x.gmPctChange <= -0.005 ? "bad" : x.gmPctChange >= 0.005 ? "good" : ""}`}>{x.gmPctChange === null ? "New" : `${x.gmPctChange >= 0 ? "▲" : "▼"} ${Math.abs(x.gmPctChange * 100).toFixed(1)} pts`}</td>
              <td className="r">{n2(x.avgUnitPrice)}</td><td className="r">{n2(x.avgUnitCost)}</td><td>{x.abc}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </section>
  );
}

function Heatmap({ d }: { d: SalesAnalytics }) {
  const h = d.heatmap!;
  const t = (d.marginTarget ?? 0.3);
  const band = (v: number | null) => (v === null ? "none" : v < t - 0.03 ? "b2" : v < t ? "b1" : v < t + 0.03 ? "a1" : v < t + 0.06 ? "a2" : "a3");
  const slipping = h.rows.filter((r) => { const c = r.cells.filter((v): v is number => v !== null); return c.length >= 3 && c[c.length - 1] < c[0] - 0.02; }).length;
  if (!h.rows.length) return null;
  const tp = (t * 100).toFixed(0);
  return (
    <section className="panel">
      <div className="panel-h">
        <div><h2 className="ph">Margin % by item and month</h2><p className="chart-sub">Orange cells are below the {tp}% target. {slipping ? `${slipping} of the top ${h.rows.length} items lost more than 2 points over these months.` : "No top item lost more than 2 points over these months."}</p></div>
        <span className="legend">
          <span><i className="hm b2" />Below {Number(tp) - 3}</span><span><i className="hm b1" />{Number(tp) - 3} to {tp}</span><span><i className="hm a1" />{tp} to {Number(tp) + 3}</span><span><i className="hm a2" />{Number(tp) + 3} to {Number(tp) + 6}</span><span><i className="hm a3" />{Number(tp) + 6} and over</span>
        </span>
      </div>
      <div className="table-wrap pad-x pad-b">
        <div className="heat" style={{ gridTemplateColumns: `minmax(200px, 260px) repeat(${h.periods.length}, minmax(56px, 1fr))` }}>
          <span />{h.periods.map((p) => <span key={p.period} className="heat-h">{p.label}</span>)}
          {h.rows.map((r) => (
            <div key={r.itemNo} style={{ display: "contents" }}>
              <span className="heat-l">{r.itemNo} {r.description}</span>
              {r.cells.map((v, i) => <span key={i} className={`heat-c hm ${band(v)}`} title={`${r.itemNo}, ${h.periods[i].label}: ${v === null ? "no sales" : `${p1(v)}%`}`}>{v === null ? "" : p1(v)}</span>)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SalesAnalyticsPage() {
  const [params, setParams] = useSearchParams();
  const q = { period: params.get("period") ?? "last12", groupBy: params.get("groupBy") ?? "month", compare: params.get("compare") ?? "py", end: params.get("end") ?? "", category: params.get("category") ?? "", customer: params.get("customer") ?? "", location: params.get("location") ?? "" };
  const { data, error, reload } = useLoad(() => api.salesAnalytics(q), [params.toString()]);
  const sync = useSync(() => reload());
  const setParam = (k: string, v: string) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p, { replace: true }); };
  useEffect(() => { document.title = "Sales Analytics · courageMFG"; }, []);

  const d = data;
  const ve = d?.sync.find((s) => s.entity === "value_entries");
  const veMissing = ve?.source === "unavailable";
  const lastSync = d?.sync.reduce((m, s) => (s.synced_at > m ? s.synced_at : m), "");
  const cmpLabel = q.compare === "pp" ? "prior period" : "prior year";
  const k = d?.kpis;
  const sel = (name: string, label: string, opts: [string, string][], width = 140) => (
    <label className="filter">{label}
      <select className="select" style={{ minWidth: width }} value={q[name as keyof typeof q]} onChange={(e) => setParam(name, e.target.value)}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
    </label>
  );

  return (
    <div className="page bc-page">
      <div className="bc-title">
        <h1>Sales Analytics</h1>
        <span className="sub">Item sales, quantity and margin from {d?.source === "item_ledger_entries" ? "Business Central item ledger entries" : "Business Central value entries and item ledger entries"}{d?.asOf ? `, through ${usDate(d.asOf)}` : ""}.{lastSync ? ` Copied from Business Central ${ago(lastSync)}.` : ""}</span>
      </div>

      {sync.running && <div className="notify info" role="status"><span className="spin" aria-hidden="true" />{sync.text}</div>}
      {!!sync.error && <ErrorBox error={sync.error instanceof ApiError && sync.error.body?.code === "consent_required" ? new Error("Business Central needs an admin to grant courageMFG access (one-time consent).") : sync.error} />}
      {veMissing && (
        <div className="notify warn" role="status">
          <span><b>Value entries aren't available yet, so margins use item ledger entry amounts.</b> In Business Central, open Web Services, add Object Type Page, Object ID 5802 (Value Entries), Service Name ValueEntries, select Published, then copy again.</span>
          <a href={bcUrl(810)} target="_blank" rel="noopener noreferrer">Open Web Services</a>
        </div>
      )}

      <div className="panel filters" role="group" aria-label="Filters">
        {sel("period", "Period", [["last12", "Last 12 Months"], ["ytd", "Year to Date"], ["last6", "Last 6 Months"], ["last3", "Last 3 Months"]])}
        {sel("end", "Ending", [["", d?.endMonth && !q.end ? `${monthName(d.endMonth)} (latest)` : "Latest month"], ...(d?.monthsWithSales ?? []).map((m) => [m, monthName(m)] as [string, string])], 150)}
        {sel("groupBy", "Group By", [["month", "Month"], ["quarter", "Quarter"]], 110)}
        {sel("compare", "Compare To", [["py", "Prior Year"], ["pp", "Prior Period"]], 130)}
        {sel("category", "Item Category", [["", "All Categories"], ...(d?.filters?.categories ?? []).map((c) => [c, c] as [string, string])])}
        {sel("customer", "Customer", [["", "All Customers"], ...(d?.filters?.customers ?? []).map((c) => [c.number, `${c.number} ${c.name}`] as [string, string])], 180)}
        {sel("location", "Location", [["", "All Locations"], ...(d?.filters?.locations ?? []).map((c) => [c, c] as [string, string])], 120)}
        <span className="spacer" />
        <button className="btn" disabled={sync.running} onClick={() => sync.run(false)}>{d?.empty ? "Copy from Business Central" : "Refresh from Business Central"}</button>
        <button className="btn" disabled={!d?.items?.length} onClick={() => exportCsv(d!.items!, `item-sales-${d!.range!.from}-to-${d!.range!.to}.csv`)}>Open in Excel</button>
      </div>

      {!!d?.laterMonths && !q.end && (
        <div className="notify info" role="status"><span>Business Central also has sales dated after today in {d.laterMonths} {d.laterMonths === 1 ? "month" : "months"}. Choose a month under Ending to include them.</span></div>
      )}
      <ErrorBox error={error} />
      {!d && !error && <div className="panel empty">Loading sales analytics…</div>}
      {d?.empty && (
        <div className="panel empty">
          <p style={{ margin: "0 0 12px" }}><b>No Business Central sales have been copied yet.</b></p>
          <p style={{ margin: "0 0 16px" }}>Copying reads items, customers, value entries and item ledger entries from Business Central as you, using your Business Central permissions.</p>
          <button className="btn primary" disabled={sync.running} onClick={() => sync.run(false)}>Copy from Business Central</button>
        </div>
      )}

      {d && !d.empty && k && (
        <>
          <div className="cues">
            <div className="cue"><span className="cue-l">Net Sales (USD)</span><span className="cue-v">{n0(k.netSales)}</span><Delta v={rel(k.netSales, k.cmpNetSales)} label={cmpLabel} /></div>
            <div className="cue"><span className="cue-l">{k.qtyShipped !== null ? "Quantity Shipped" : "Quantity Invoiced"}</span><span className="cue-v">{n0(k.qtyShipped ?? k.qtyInvoiced)}</span><Delta v={k.qtyShipped !== null ? rel(k.qtyShipped, k.cmpQtyShipped ?? 0) : rel(k.qtyInvoiced, k.cmpQtyInvoiced)} label={cmpLabel} /></div>
            <div className="cue"><span className="cue-l">Gross Margin (USD)</span><span className="cue-v">{n0(k.grossMargin)}</span><Delta v={rel(k.grossMargin, k.cmpGrossMargin)} label={cmpLabel} /></div>
            <div className="cue"><span className="cue-l">Gross Margin %</span><span className="cue-v">{p1(k.gmPct)}</span><Delta v={k.gmPct !== null && k.cmpGmPct !== null ? k.gmPct - k.cmpGmPct : null} pts label={cmpLabel} /></div>
            <div className="cue"><span className="cue-l">Items Sold</span><span className="cue-v">{n0(k.itemsSold)}</span><span className="cue-n">{k.paretoShare !== null ? `Top ${k.paretoItems} ${k.paretoItems === 1 ? "item earns" : "items earn"} ${(k.paretoShare * 100).toFixed(0)}% of margin` : ""}</span></div>
          </div>

          <div className="split">
            <section className="panel pad grow">
              <SalesChart d={d} />
              <MarginChart d={d} />
            </section>
            <div className="side">
              <Bridge d={d} />
              <Attention d={d} setParam={setParam} />
            </div>
          </div>

          <ItemTable d={d} />
          <Heatmap d={d} />
        </>
      )}
    </div>
  );
}
